import * as Sentry from "@sentry/react";

const IGNORED_ERROR_PATTERNS: Array<string | RegExp> = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  "Non-Error promise rejection captured",
  "Failed to fetch dynamically imported module",
  /Loading chunk \d+ failed/,
  /Loading CSS chunk \d+ failed/,
  /^Network request failed/,
  /^TypeError: Failed to fetch$/,
  /Object captured as exception with keys: __sentry_xhr/,
];

const EMAIL_REGEX = /([a-zA-Z0-9._-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

function scrubEmails(value: unknown): unknown {
  if (typeof value === "string") {
    return value.replace(EMAIL_REGEX, "[email]");
  }
  if (Array.isArray(value)) {
    return value.map(scrubEmails);
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = scrubEmails(v);
    }
    return out;
  }
  return value;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT ||
    import.meta.env.MODE ||
    "production";
  const release = import.meta.env.VITE_RELEASE || undefined;
  const isProd = environment === "production";

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      }),
    ],
    tracesSampleRate: isProd ? 0.1 : 1.0,
    replaysSessionSampleRate: isProd ? 0.05 : 0.2,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [
      /^\//,
      /^https:\/\/eqousueppvnlgozawmze\.supabase\.co/,
      /^https:\/\/fjdlzwxbeydfwkcwwjim\.supabase\.co/,
    ],
    ignoreErrors: IGNORED_ERROR_PATTERNS,
    beforeSend(event) {
      if (event.extra) event.extra = scrubEmails(event.extra) as typeof event.extra;
      if (event.tags) event.tags = scrubEmails(event.tags) as typeof event.tags;
      if (event.contexts) event.contexts = scrubEmails(event.contexts) as typeof event.contexts;
      return event;
    },
  });
}

export function setUser(user: { id: string; email?: string }) {
  Sentry.setUser(user);
}

export function clearUser() {
  Sentry.setUser(null);
}

export function captureException(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, { extra: context });
}

export { Sentry };
